import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FiLock } from 'react-icons/fi';

type FooterLinkTarget =
  | { kind: 'route'; to: string }
  | { kind: 'anchor'; to: string; anchor: string; pendingPhase: number };

interface FooterLink {
  key: string;
  target: FooterLinkTarget;
}

const route = (to: string): FooterLinkTarget => ({ kind: 'route', to });
const anchor = (to: string, anchorName: string, pendingPhase: number): FooterLinkTarget => ({
  kind: 'anchor',
  to,
  anchor: anchorName,
  pendingPhase,
});

const FOOTER_COLUMNS: { headingKey: string; links: FooterLink[] }[] = [
  {
    headingKey: 'learn',
    links: [
      { key: 'all_courses', target: route('/courses') },
      { key: 'free_courses', target: route('/courses') },
      { key: 'skill_paths', target: route('/courses') },
      { key: 'live_classes', target: anchor('/', 'batches', 9) },
      { key: 'free_masterclasses', target: anchor('/', 'demos', 9) },
      { key: 'cv_builder', target: route('/cv-builder') },
      { key: 'leaderboard', target: route('/leaderboard') },
      { key: 'free_notes', target: route('/guides') },
      { key: 'free_downloads', target: route('/resources') },
      { key: 'instructors', target: route('/instructors') },
      { key: 'book_store', target: route('/store') },
      { key: 'learning_centres', target: route('/centres') },
    ],
  },
  {
    headingKey: 'company',
    links: [
      { key: 'about_us', target: route('/page/about-us') },
      { key: 'become_instructor', target: route('/page/become-an-instructor') },
      { key: 'careers', target: route('/page/careers') },
      { key: 'contact', target: route('/page/contact') },
      { key: 'for_business', target: route('/page/for-business') },
    ],
  },
  {
    headingKey: 'support',
    links: [
      { key: 'verify_certificate', target: route('/verify') },
      { key: 'help_center', target: route('/page/help-center') },
      { key: 'terms', target: route('/page/terms-of-service') },
      { key: 'privacy', target: route('/page/privacy-policy') },
      { key: 'refund', target: route('/page/refund-policy') },
    ],
  },
];

function hrefOf(target: FooterLinkTarget): string {
  return target.kind === 'anchor' ? `${target.to}#${target.anchor}` : target.to;
}

/**
 * Footer — the COMPOSED result of five blocks (base :558 + v2/v3/v11/v12/v13),
 * 22 links (affiliate link currently omitted from the public footer).
 *
 * Every link carries a real destination declared at source. The prototype had
 * ten with no handler at all, repaired only at v21 by matching link TEXT and
 * patching the DOM afterwards (:6584) — which breaks silently the moment a
 * label is reworded.
 */
export function Footer() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <footer
      className="relative border-t border-[#2e2620] bg-[#161311] pt-[54px] pb-[30px] text-[#ababab] shadow-[0_-10px_36px_rgba(22,19,17,.12)]"
      style={{
        background:
          'radial-gradient(ellipse at 50% 135%, rgba(244, 80, 42, 0.32), transparent 60%), #161311',
      }}
    >
      <div className="container relative mx-auto px-[22px]">
        <div className="mb-[34px] grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-[34px] max-[980px]:grid-cols-2" data-testid="footer-links-grid">
          <div>
            <div className="mb-3 flex cursor-pointer items-center">
              <img src="/logo_black.png" alt="MAKTech Academy" className="h-36 w-auto object-contain" />
            </div>
            <p className="max-w-[280px] text-[13.5px] text-[#ababab]">{t('footer.tagline')}</p>
          </div>

          {FOOTER_COLUMNS.map((col) => (
            <div key={col.headingKey}>
              <h4 className="mb-3.5 font-['Inter',sans-serif] text-sm font-semibold text-white">
                {t(`footer.${col.headingKey}`)}
              </h4>
              {col.links.map((l) => (
                <Link
                  key={l.key}
                  to={hrefOf(l.target)}
                  data-testid={`foot-link-${l.key}`}
                  data-pending={l.target.kind !== 'route' ? String(l.target.pendingPhase) : undefined}
                  className="block cursor-pointer py-1 text-[13.5px] text-[#ababab] transition-colors hover:text-white"
                >
                  {t(`footer.${l.key}`)}
                </Link>
              ))}
            </div>
          ))}
        </div>
        {/* Payment trust strip — injectV3 (:2195), before foot-bottom. */}
        <div className="mt-3.5 flex flex-wrap items-center gap-2">
          <span className="text-xs text-[#8c8c8c]">{t('footer.we_accept')}</span>
          {['bKash', 'Nagad', 'Rocket', 'VISA', 'Mastercard', 'AMEX', 'PayPal'].map((method) => (
            <span className="rounded-lg border border-[#3a3a3a] bg-[#2a2a2a] px-3 py-[5px] text-xs font-extrabold text-[#cfcfcf]" key={method}>{method}</span>
          ))}
          <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-[#8c8c8c]">
            <FiLock className="size-3.5 shrink-0" aria-hidden />
            {t('footer.ssl_note')}
          </span>
        </div>
        <div className="mt-[34px] flex flex-wrap justify-between gap-2.5 border-t border-[#3a3a3a] pt-[22px] text-[13px]">
          <span>{t('footer.copyright', { year })}</span>
          <span>{t('footer.demo_note')}</span>
        </div>
      </div>
    </footer>
  );
}
